import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useAuth } from "../login/AuthProvider";
import { member_search, member_edit } from "../../components/apipath";
import SwitchBox from "../../components/switchBox";
import { switchHelper, dateHelper, textHelper } from "../../modules/helpers";
import PageList from "../../components/pageList";

function AdmMemberView() {
  const [memberlist, setMemberlist] = useState([]);
  const [conditions, setConditions] = useState(null);
  const [rowsCount, setRowsCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [prodOpen, setProdOpen] = useState(false);
  const [prodSearch, setProdSearch] = useState("");

  const { token } = useAuth();

  const memberStatus = ["請選擇", "在職者", "在學生", "全職考生", "待業中"];
  const memberAccountStatus = ["請選擇", "停用", "啟用"];

  const products = [
    "請選擇",
    "CPA專業方案",
    "CPA全修方案",
    "CPA選修方案",
    "CPA自修方案",
    "CPA單科雙語",
    "CMA自修方案",
    "CMA菁英方案",
    "EA速成方案",
    "EA完勝方案",
    "CSU學分班",
    "CPE select",
    "個人所得稅",
    "財產交易稅",
    "公司所得稅",
    "穿透實體稅務",
    "衝刺方案",
    "職涯無限方案",
    "CPE Essentials",
    "全修舊生",
    "選修舊生",
    "自修舊生",
    "自修初階",
    "美稅訓練營兩日方案",
    "美稅訓練營 Day 2 方案",
  ];

  const PRODUCT_OPTIONS = products
    .map((label, idx) => ({ id: String(idx), label }))
    .filter((opt) => opt.id !== "0");

  const thisYear = new Date().getFullYear();

  const yearList = (y) => {
    const years = ["請選擇"];
    for (let i = y; i >= 2017; i--) years.push(i);
    return years;
  };

  useEffect(() => {
    setConditions({
      year: null,
      product_id: [],
      user_type: null,
      pass: null,
      refund: null,
      chinese_name: null,
      email: null,
      account_status: null,
      status: null,
      mem_status: null,
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const wrapper = document.getElementById("member-product-filter");
      if (wrapper && !wrapper.contains(e.target)) {
        setProdOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const makePayload = () => {
    const payload = {
      year:
        conditions?.year === "" || conditions?.year == null
          ? null
          : Number(conditions.year),

      product_id:
        Array.isArray(conditions?.product_id) && conditions.product_id.length > 0
          ? conditions.product_id.map((id) => Number(id))
          : null,

      user_type: conditions?.user_type || null,

      pass:
        typeof conditions?.pass === "boolean"
          ? conditions.pass
            ? 1
            : 0
          : conditions?.pass ?? null,

      refund:
        typeof conditions?.refund === "boolean"
          ? conditions.refund
            ? 1
            : 0
          : conditions?.refund ?? null,

      chinese_name: conditions?.chinese_name?.trim() || null,
      email: conditions?.email?.trim() || null,

      account_status:
        conditions?.account_status === "" || conditions?.account_status == null
          ? null
          : Number(conditions.account_status),

      status:
        conditions?.status === "" || conditions?.status == null
          ? null
          : Number(conditions.status),

      mem_status:
        typeof conditions?.mem_status === "boolean"
          ? conditions.mem_status
            ? 1
            : 0
          : conditions?.mem_status ?? null,
    };

    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });

    return payload;
  };

  const fetchData = async (page = 1, rows = 50) => {
    try {
      setLoading(true);
      if (page <= 0) page = 1;

      const payload = makePayload();

      const { data: resp } = await axios.post(member_search(page - 1, rows), payload, {
        headers: { token },
      });

      const list = resp?.[0]?.data || [];
      const cleaned = list.filter(
        (row) => row && Object.keys(row).length > 0 && row.mem_id != null
      );

      setMemberlist(cleaned);

      const total = resp?.[1]?.data?.[0]?.total ?? 0;
      setRowsCount(total);
    } catch (err) {
      console.error("fetchData error:", err);
      setMemberlist([]);
      setRowsCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;

    if (type === "checkbox") {
      setConditions((prev) => ({
        ...prev,
        [name]: switchHelper(checked),
      }));
    } else {
      setConditions((prev) => ({
        ...prev,
        [name]: value === "" ? null : value,
      }));
    }
  };

  const handleSearch = () => {
    fetchData(1, 50);
  };

  const formatDateOnly = (value) => {
    if (!value) return "";
    if (typeof value === "string" && value.includes("T")) {
      return value.split("T")[0];
    }
    return value;
  };

  const productIds = conditions?.product_id || [];

  const filteredProducts = PRODUCT_OPTIONS.filter((p) => {
    const kw = prodSearch.trim().toLowerCase();
    if (!kw) return true;

    return (
      String(p.id).includes(kw) ||
      String(p.label).toLowerCase().includes(kw)
    );
  });

  const allChecked =
    filteredProducts.length > 0 &&
    filteredProducts.every((p) => productIds.map(String).includes(String(p.id)));

  const toggleOneProduct = (id, checked) => {
    setConditions((prev) => {
      const current = prev.product_id || [];
      return {
        ...prev,
        product_id: checked
          ? [...new Set([...current, String(id)])]
          : current.filter((v) => String(v) !== String(id)),
      };
    });
  };

  const toggleAllProducts = (checked) => {
    if (checked) {
      setConditions((prev) => ({
        ...prev,
        product_id: filteredProducts.map((p) => String(p.id)),
      }));
    } else {
      setConditions((prev) => ({
        ...prev,
        product_id: [],
      }));
    }
  };

  const selectedProductLabels = PRODUCT_OPTIONS
    .filter((item) => productIds.includes(item.id))
    .map((item) => item.label);

  const productButtonText =
    selectedProductLabels.length === 0
      ? "請選擇方案"
      : selectedProductLabels.length <= 2
      ? selectedProductLabels.join("、")
      : `已選 ${selectedProductLabels.length} 項方案`;

  const handleExportExcel = () => {
    if (!memberlist || memberlist.length === 0) {
      alert("目前沒有可匯出的資料");
      return;
    }

    const exportData = memberlist.map((member) => ({
      姓名: member.chinese_name || "",
      信箱: member.email || "",
      創建日期: member.createtime ? dateHelper(member.createtime, "date") : "",
      到期日期: member.active_date ? dateHelper(member.active_date, "date") : "",
      諮詢日期: formatDateOnly(member.consult_date),
      選洲: member.selected_state || "",
      認證: member.edu_verified || "",
      報考: member.exam_registered || "",
      預約: member.test_center_booked || "",
      方案: member.product_name || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "會員總表");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileData = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const today = new Date().toISOString().split("T")[0];
    saveAs(fileData, `會員總表_${today}.xlsx`);
  };

  return (
    <>
      <div className="container-fluid p-5 mt-5">
        {conditions && (
          <>
            <div className="row g-2 mb-2">
              <div className="col-2">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">年度</span>
                  <select
                    className="form-select form-select-sm"
                    name="year"
                    value={textHelper(conditions.year)}
                    onChange={handleInputChange}
                  >
                    {yearList(thisYear).map((value) => (
                      <option key={value} value={value === "請選擇" ? "" : value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                className="col-xl-4 col-md-6 col-12"
                id="member-product-filter"
              >
                <div
                  className="form-group"
                  style={{ position: "relative", marginBottom: 0 }}
                >
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">方案</span>
                    <button
                      type="button"
                      className="form-control text-start"
                      onClick={() => setProdOpen((v) => !v)}
                      style={{
                        background: "#fff",
                        border: "1px solid #ced4da",
                        borderLeft: 0,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      }}
                    >
                      <span>{productButtonText}</span>
                      <span style={{ float: "right" }}>{prodOpen ? "▲" : "▼"}</span>
                    </button>
                  </div>

                  {prodOpen && (
                    <div
                      className="panel panel-default filter-popover"
                      style={{
                        position: "absolute",
                        zIndex: 1050,
                        top: "110%",
                        right: 0,
                        left: "auto",
                        width: "100%",
                        minWidth: 320,
                        maxHeight: 360,
                        overflow: "hidden",
                        background: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: 4,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}
                    >
                      <div className="panel-heading" style={{ padding: "8px 10px" }}>
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="搜尋方案名稱或ID"
                            value={prodSearch}
                            onChange={(e) => setProdSearch(e.target.value)}
                          />
                          <span className="input-group-btn">
                            <button
                              className="btn btn-default"
                              onClick={() => setProdSearch("")}
                              type="button"
                            >
                              清除
                            </button>
                          </span>
                        </div>

                        <div style={{ marginTop: 8 }}>
                          <label
                            className="checkbox-inline"
                            style={{ userSelect: "none", marginBottom: 0 }}
                          >
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={(e) => toggleAllProducts(e.target.checked)}
                            />{" "}
                            全選/清空
                          </label>
                        </div>
                      </div>

                      <div
                        className="panel-body"
                        style={{ padding: 0, maxHeight: 230, overflow: "auto" }}
                      >
                        <ul className="list-group" style={{ marginBottom: 0 }}>
                          {filteredProducts.map((p) => {
                            const id = String(p.id);
                            const checked = productIds.map(String).includes(id);

                            return (
                              <li
                                key={id}
                                className="list-group-item"
                                style={{
                                  padding: "6px 10px",
                                  backgroundColor: "#fff",
                                  color: "#333",
                                  cursor: "pointer",
                                }}
                              >
                                <label
                                  htmlFor={`product-${id}`}
                                  style={{
                                    display: "block",
                                    fontWeight: "normal",
                                    margin: 0,
                                    color: "#333",
                                    cursor: "pointer",
                                    userSelect: "auto",
                                  }}
                                >
                                  <input
                                    id={`product-${id}`}
                                    type="checkbox"
                                    className="m-r-5"
                                    checked={checked}
                                    onChange={(e) => toggleOneProduct(id, e.target.checked)}
                                    style={{ marginRight: 6 }}
                                  />
                                  [{id}] {p.label}
                                </label>
                              </li>
                            );
                          })}

                          {filteredProducts.length === 0 && (
                            <li className="list-group-item text-muted">無符合方案</li>
                          )}
                        </ul>
                      </div>

                      <div
                        className="panel-footer text-right"
                        style={{ padding: "8px 10px" }}
                      >
                        <button
                          type="button"
                          className="btn btn-default btn-sm m-r-5"
                          onClick={() => setProdOpen(false)}
                        >
                          關閉
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setProdOpen(false);
                            handleSearch();
                          }}
                        >
                          套用
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* <div className="col-xl-2 col-md-3 col-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">狀態</span>
                  <select
                    className="form-select form-select-sm"
                    name="status"
                    value={textHelper(conditions.status)}
                    onChange={handleInputChange}
                  >
                    {memberStatus.map((value, index) => (
                      <option key={index} value={index === 0 ? "" : index}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div> */}

              <div className="col-xl-2 col-md-3 col-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">帳戶狀態</span>
                  <select
                    className="form-select form-select-sm"
                    name="account_status"
                    value={textHelper(conditions.account_status)}
                    onChange={handleInputChange}
                  >
                    {memberAccountStatus.map((value, index) => (
                      <option key={index} value={index === 0 ? "" : index - 1}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-xl-1 col-md-2 col-4">
                <div className="input-group input-group-sm">
                  {SwitchBox("refund", conditions.refund, handleInputChange, "已退費")}
                </div>
              </div>

              <div className="col-xl-1 col-md-2 col-4">
                <div className="input-group input-group-sm">
                  {SwitchBox("mem_status", conditions.mem_status, handleInputChange, "學員")}
                </div>
              </div>
            </div>

            <div className="row g-2">
              <div className="col-xl-3 col-md-4 col-12">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">姓名</span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="輸入姓名"
                    name="chinese_name"
                    value={textHelper(conditions.chinese_name)}
                    onChange={handleInputChange}
                    maxLength={200}
                  />
                </div>
              </div>

              <div className="col-xl-3 col-md-4 col-12">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">Email</span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="輸入 Email"
                    name="email"
                    value={textHelper(conditions.email)}
                    onChange={handleInputChange}
                    maxLength={200}
                  />
                </div>
              </div>

              <div className="col-xl-3 col-md-4 col-12 text-xl-end text-md-end text-sm-center">
                <button
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? "搜尋中…" : "搜尋"}
                </button>

                <button
                  className="btn btn-success btn-sm"
                  onClick={handleExportExcel}
                  disabled={loading || memberlist.length === 0}
                >
                  匯出 Excel
                </button>
              </div>
            </div>
          </>
        )}

        <div className="mt-2 table-responsive">
          <table className="table table-striped border table-hover">
            <thead>
              <tr>
                <td>姓名</td>
                <td>信箱</td>
                <td>創建日期</td>
                <td>到期日期</td>
                <td>諮詢日期</td>
                <td>選洲</td>
                <td>認證</td>
                <td>報考</td>
                <td>預約</td>
                <td>進入</td>
              </tr>
            </thead>
            <tbody>
              {memberlist.map((member) =>
                member.mem_id ? (
                  <tr key={member.mem_id}>
                    <td>{member.chinese_name}</td>
                    <td>{member.email}</td>
                    <td>{dateHelper(member.createtime, "date")}</td>
                    <td>{dateHelper(member.active_date, "date")}</td>
                    <td>{formatDateOnly(member.consult_date)}</td>
                    <td>{member.selected_state}</td>
                    <td>{member.edu_verified}</td>
                    <td>{member.exam_registered}</td>
                    <td>{member.test_center_booked}</td>
                    <td>
                      <Link
                        className="btn btn-primary btn-sm"
                        to={`/adm/admmemberedit/${member.mem_id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        點擊
                      </Link>
                    </td>
                  </tr>
                ) : null
              )}

              {!loading && memberlist.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    無資料
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="m-2 p-2">{PageList(rowsCount, fetchData)}</div>
      </div>
    </>
  );
}

export default AdmMemberView;